import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, timeout } from 'rxjs';
import { API_BASE_URL } from '../../tokens/api.tokens';

export interface QueryParams {
  [key: string]:
    | string
    | number
    | boolean
    | readonly (string | number | boolean)[]
    | undefined
    | null;
}

export interface ApiRequestOptions {
  readonly bypassCache?: boolean;
  readonly headers?: Record<string, string>;
}

const TIMEOUT_OVERRIDES: ReadonlyArray<readonly [string, number]> = [
  ['/jobs/listing/paginated', 15_000],
  ['/jobs/listing/featured', 5_000],
  ['/jobs/skills/autocomplete', 2_000],
  ['/creators/listing/paginated', 15_000],  // Increased from 5s to 15s for complex queries
  ['/creators/skills', 5_000],
  ['/jobs/locations', 5_000],
  ['/jobs/industries', 5_000],
  ['/job-posts/', 5_000],
  ['/brands/', 5_000],
  ['/creators/saved-jobs', 5_000],
  ['/creators/applied-jobs', 5_000],
];
const UPLOAD_TIMEOUT_MS = 300_000;
const DEFAULT_TIMEOUT_MS = 10_000;

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  get<T>(endpoint: string, params?: QueryParams, options?: ApiRequestOptions): Observable<T> {
    const httpParams = this.buildParams(params);
    const headers = this.buildHeaders(options);
    return this.http
      .get<T>(`${this.baseUrl}${endpoint}`, { params: httpParams, headers })
      .pipe(timeout(this.resolveTimeout(endpoint)));
  }

  post<T>(endpoint: string, body: unknown, params?: QueryParams): Observable<T> {
    const httpParams = this.buildParams(params);
    return this.http
      .post<T>(`${this.baseUrl}${endpoint}`, body, { params: httpParams })
      .pipe(timeout(this.resolveTimeout(endpoint)));
  }

  postFormData<T>(endpoint: string, formData: FormData): Observable<T> {
    return this.http
      .post<T>(`${this.baseUrl}${endpoint}`, formData)
      .pipe(timeout(UPLOAD_TIMEOUT_MS));
  }

  put<T>(endpoint: string, body: unknown, params?: QueryParams): Observable<T> {
    const httpParams = this.buildParams(params);
    return this.http
      .put<T>(`${this.baseUrl}${endpoint}`, body, { params: httpParams })
      .pipe(timeout(this.resolveTimeout(endpoint)));
  }

  patch<T>(endpoint: string, body: unknown, params?: QueryParams): Observable<T> {
    const httpParams = this.buildParams(params);
    return this.http
      .patch<T>(`${this.baseUrl}${endpoint}`, body, { params: httpParams })
      .pipe(timeout(this.resolveTimeout(endpoint)));
  }

  delete<T>(endpoint: string, params?: QueryParams): Observable<T> {
    const httpParams = this.buildParams(params);
    return this.http
      .delete<T>(`${this.baseUrl}${endpoint}`, { params: httpParams })
      .pipe(timeout(this.resolveTimeout(endpoint)));
  }

  private resolveTimeout(endpoint: string): number {
    for (const [prefix, ms] of TIMEOUT_OVERRIDES) {
      if (endpoint.startsWith(prefix)) return ms;
    }
    return DEFAULT_TIMEOUT_MS;
  }

  private buildParams(params?: QueryParams): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            httpParams = httpParams.append(key, String(item));
          });
        } else if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }
    return httpParams;
  }

  private buildHeaders(options?: ApiRequestOptions): HttpHeaders {
    let headers = new HttpHeaders(options?.headers ?? {});

    if (options?.bypassCache) {
      headers = headers
        .set('X-Cache-Bypass', 'true')
        .set('Cache-Control', 'no-cache')
        .set('Pragma', 'no-cache');
    }

    return headers;
  }
}
